import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { advertisementAPI, locationAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import { resolveImageUrl } from '../../../utils/imageUtils';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { 
  X, Upload, FileText, Globe, Calendar, Save, ArrowLeft
} from 'lucide-react';
import './AdvertisementForm.css';

const AdvertisementForm = ({ ad, defaultCountryCode, onSuccess, onCancel }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);
  
  const [formData, setFormData] = useState({
    advertisement_id: '',
    title_en: '',
    title_ar: '',
    description_en: '',
    description_ar: '',
    country_code: defaultCountryCode || '',
    route: '',
    expiry_date: '',
    is_active: true
  });

  const [selectedMedia, setSelectedMedia] = useState(null); // New file
  const [existingMedia, setExistingMedia] = useState([]); // Saved files
  const [mediaToRemove, setMediaToRemove] = useState([]);

  useEffect(() => {
    fetchCountries();
    
    if (ad) {
      if (ad.isClone) {
        // Handle Cloned Data
        setFormData({
          advertisement_id: '', // Will be auto-generated below for new ads
          title_en: ad.title_en || '',
          title_ar: ad.title_ar || '',
          description_en: ad.description_en || '',
          description_ar: ad.description_ar || '',
          country_code: ad.country_code || defaultCountryCode || '',
          route: ad.route || '',
          expiry_date: ad.expiry_date ? ad.expiry_date.split(' ')[0] : '',
          runtime_duration: ad.runtime_duration || '',
          is_active: ad.is_active ?? true
        });
        setExistingMedia(ad.media || []);
      } else {
        // Handle Regular Edit
        setFormData({
          advertisement_id: ad.advertisement_id || '',
          title_en: ad.title_en || '',
          title_ar: ad.title_ar || '',
          description_en: ad.description_en || '',
          description_ar: ad.description_ar || '',
          country_code: ad.country_code || '',
          route: ad.route || '',
          expiry_date: ad.expiry_date ? ad.expiry_date.split(' ')[0] : '',
          runtime_duration: ad.runtime_duration || '',
          is_active: ad.is_active ?? true
        });
        setExistingMedia(ad.media || []);
      }
    } else {
      // Generate automatic ID for new ads
      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:T]/g, '').split('.')[0];
      const autoId = `AD_${timestamp}`;
      setFormData(prev => ({ ...prev, advertisement_id: autoId }));
    }

    // Auto-generate ID for clones too if its empty
    if (ad && ad.isClone) {
        const now = new Date();
        const timestamp = now.toISOString().replace(/[-:T]/g, '').split('.')[0];
        const autoId = `AD_${timestamp}`;
        setFormData(prev => ({ ...prev, advertisement_id: autoId }));
    }
  }, [ad]);

  // Initialize Flatpickr
  useEffect(() => {
    if (dateInputRef.current) {
      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        defaultDate: formData.expiry_date,
        onChange: (selectedDates, dateStr) => {
          setFormData(prev => ({ ...prev, expiry_date: dateStr }));
        }
      });
    }
    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await locationAPI.getCountries();
      setCountries(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch countries');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for runtime_duration to enforce min 1
    let finalValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedMedia(file);
      
      // Automatically mark existing ones for removal if a new one is selected
      if (existingMedia.length > 0) {
        setMediaToRemove(prev => [...prev, ...existingMedia.map(m => m.id)]);
        setExistingMedia([]);
      }
    }
  };

  const removeSelectedFile = () => {
    setSelectedMedia(null);
  };

  const removeExistingMedia = (mediaId) => {
    setMediaToRemove(prev => [...prev, mediaId]);
    setExistingMedia(prev => prev.filter(m => m.id !== mediaId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        media: selectedMedia,
        remove_media_ids: mediaToRemove
      };

      if (ad && !ad.isClone) {
        await advertisementAPI.updateAdvertisement(ad.id, data);
        showSuccessToast(t('success.updated'));
      } else {
        // Handle both "new" and "cloned" creations
        if (ad?.isClone) {
          data.source_ad_id = ad.source_ad_id;
        }
        await advertisementAPI.createAdvertisement(data);
        showSuccessToast(t('success.created'));
      }
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.message || t('error.saveFailed');
      showErrorToast(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ad-form-view">
      {/* ── Page Header ── */}
      <div className="form-header">
        <div className="header-left">
          <button className="back-btn" onClick={onCancel}>
            <ArrowLeft size={18} />
            {t('adminDashboard.cms.back_to_list', 'Back to List')}
          </button>
          <h3>{ad && !ad.isClone ? t('adminDashboard.cms.edit_advertisement', 'Edit Advertisement') : t('adminDashboard.cms.add_advertisement', 'Add Advertisement')}</h3>
        </div>

        <div className="active-switch-row">
          <span className={`switch-label ${!formData.is_active ? 'switch-label--active' : ''}`}>
            {t('adminDashboard.cms.inactive', 'Inactive')}
          </span>
          <label className="status-toggle">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
            />
            <span className="status-toggle-track"></span>
          </label>
          <span className={`switch-label ${formData.is_active ? 'switch-label--active' : ''}`}>
            {t('adminDashboard.cms.active', 'Active')}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="ad-form-content">
        
        {/* ── Section: Identification & Titles ── */}
        <div className="bfc-section">
          <div className="bfc-section-header">
            <span className="bfc-section-icon"><FileText size={18} /></span>
            <span className="bfc-section-title">{t('adminDashboard.cms.ad_details', 'General Details')}</span>
          </div>
          <div className="bfc-section-body">
            <div className="form-group">
              <label>{t('adminDashboard.cms.ad_id', 'Advertisement ID')} <span className="required">*</span></label>
              <input
                type="text"
                name="advertisement_id"
                value={formData.advertisement_id}
                onChange={handleInputChange}
                placeholder="AD_YYYYMMDD_HHMMSS"
                required
                disabled
              />
            </div>

            <div className="bfc-lang-grid">
              <div className="form-group">
                <label className="lang-label">
                  <span className="lang-badge en">EN</span>
                  {t('adminDashboard.cms.title_en', 'Title (English)')}
                </label>
                <input
                  type="text"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleInputChange}
                  placeholder={t('adminDashboard.cms.enter_en_title', 'Enter English Title')}
                />
              </div>
              <div className="form-group" dir="rtl">
                <label className="lang-label">
                  <span className="lang-badge ar">AR</span>
                  {t('adminDashboard.cms.title_ar', 'Title (Arabic)')}
                </label>
                <input
                  type="text"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleInputChange}
                  placeholder={t('adminDashboard.cms.enter_ar_title', 'Enter Arabic Title')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Section: Content Description ── */}
        <div className="bfc-section">
          <div className="bfc-section-header">
            <span className="bfc-section-icon"><FileText size={18} /></span>
            <span className="bfc-section-title">{t('adminDashboard.cms.description_en', 'Description')}</span>
          </div>
          <div className="bfc-section-body">
            <div className="bfc-lang-grid">
              <div className="form-group">
                <label className="lang-label">
                  <span className="lang-badge en">EN</span>
                  {t('adminDashboard.cms.description_en', 'Description (English)')}
                </label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  placeholder={t('adminDashboard.cms.enter_en_desc', 'English Description')}
                  rows="3"
                />
              </div>
              <div className="form-group" dir="rtl">
                <label className="lang-label">
                  <span className="lang-badge ar">AR</span>
                  {t('adminDashboard.cms.description_ar', 'Description (Arabic)')}
                </label>
                <textarea
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                  placeholder={t('adminDashboard.cms.enter_ar_desc', 'Arabic Description')}
                  rows="3"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Section: Configuration ── */}
        <div className="bfc-section">
          <div className="bfc-section-header">
            <span className="bfc-section-icon"><Globe size={18} /></span>
            <span className="bfc-section-title">{t('adminDashboard.cms.ad_config_scheduling', 'Configuration & Scheduling')}</span>
          </div>
          <div className="bfc-section-body">
            <div className="form-row">
              <div className="form-group">
                <label>{t('adminDashboard.cms.country', 'Country')} <span className="required">*</span></label>
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('adminDashboard.cms.select_country', 'Select Country')}</option>
                  <option value="all">{t('adminDashboard.cms.all_countries', 'All Countries')}</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>
                      {i18n.language === 'ar' ? c.name_ar : c.name_en} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('adminDashboard.cms.expiry_date', 'Expiry Date')}</label>
                <input
                  type="text"
                  name="expiry_date"
                  ref={dateInputRef}
                  value={formData.expiry_date}
                  onChange={handleInputChange}
                  placeholder="YYYY-MM-DD"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('adminDashboard.cms.route', 'Navigation Route')}</label>
                <input
                  type="text"
                  name="route"
                  value={formData.route}
                  onChange={handleInputChange}
                  placeholder="/shop/products/..."
                />
              </div>
            </div>
          </div>
        </div>


        {/* ── Section: Media Assets ── */}
        <div className="bfc-section">
          <div className="bfc-section-header">
            <span className="bfc-section-icon"><Upload size={18} /></span>
            <span className="bfc-section-title">{t('adminDashboard.cms.ad_media', 'Media Assets')}</span>
          </div>
          <div className="bfc-section-body">
            <input
              type="file"
              id="ad-media-input"
              accept="image/*,video/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {(!selectedMedia && existingMedia.length === 0) && (
              <label htmlFor="ad-media-input" className="upload-zone">
                <div className="upload-icon"><Upload size={32} /></div>
                <p><strong>{t('adminDashboard.cms.click_to_upload', 'Click to upload media')}</strong></p>
                <p className="text-sm text-gray-500">{t('adminDashboard.cms.media_support', 'Supports Images and Videos')}</p>
              </label>
            )}

            <div className={`ad-media-preview-grid ${(selectedMedia || existingMedia.length > 0) ? 'has-media' : ''}`}>
              {existingMedia.map((media) => (
                <div key={media.id} className="media-preview-card">
                  {media.file_type === 'video' ? (
                    <video src={resolveImageUrl(media.url)} className="preview-content" />
                  ) : (
                    <img src={resolveImageUrl(media.url)} className="preview-content" alt="existing" />
                  )}
                  <button type="button" className="media-remove-btn" onClick={() => removeExistingMedia(media.id)}>
                    <X size={14} />
                  </button>
                  <div className="media-tag existing">
                    {ad?.isClone ? t('adminDashboard.cms.cloned', 'Cloned') : t('common.saved', 'Saved')}
                  </div>
                </div>
              ))}

              {selectedMedia && (
                <div className="media-preview-card">
                  {selectedMedia.type.startsWith('video') ? (
                    <div className="video-placeholder">Video Ready</div>
                  ) : (
                    <img src={URL.createObjectURL(selectedMedia)} className="preview-content" alt="selected" />
                  )}
                  <button type="button" className="media-remove-btn" onClick={removeSelectedFile}>
                    <X size={14} />
                  </button>
                  <div className="media-tag new">{t('common.new', 'New')}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Form Footer ── */}
      <div className="form-footer">
        <button type="button" onClick={onCancel} className="cancel-btn-large">
          {t('adminDashboard.cms.cancel', 'cancel')}
        </button>
        <button type="submit" disabled={loading} className="submit-btn-large">
          {loading ? <div className="spinner-small" /> : (ad && !ad.isClone ? t('adminDashboard.cms.update', 'update') : t('adminDashboard.cms.create', 'create'))}
        </button>
      </div>

      </form>
    </div>
  );
};

export default AdvertisementForm;
